import { useApplicationStore } from '@/stores/application'
import { createCheeseTracking, type CheeseSdk, type CheeseTraits } from '@/utils/cheeseTracking'
import { logger } from '@/utils/logger'

declare global {
    interface Window {
        cheese?: CheeseSdk
    }
}

export default defineNuxtPlugin({
    name: 'cheese',
    dependsOn: ['auth'],
    setup(nuxtApp) {
        const config = useRuntimeConfig().public
        const allowedHosts = String(config.cheeseAllowedHosts)
            .split(',')
            .map((host) => host.trim())
        if (
            String(config.cheeseEnabled) !== 'true' ||
            !config.cheeseWriteKey ||
            !allowedHosts.includes(window.location.hostname)
        )
            return

        const store = useApplicationStore()
        const route = useRouter().currentRoute
        const { cookiesEnabledIds } = useCookieControl()
        // First-party relay (server/routes/_cheese): browsers never contact the Cheese domain.
        const host = `${window.location.origin}/_cheese`
        const app = String(config.cheeseHost).replace(/\/$/, '')
        const privacySignal = () =>
            navigator.doNotTrack === '1' ||
            (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true ||
            (window as Window & { doNotTrack?: string }).doNotTrack === '1'

        const tracking = createCheeseTracking({
            host,
            app,
            writeKey: String(config.cheeseWriteKey),
            load: () =>
                new Promise<CheeseSdk>((resolve, reject) => {
                    if (window.cheese) return resolve(window.cheese)
                    const script = document.createElement('script')
                    script.src = `${host}/v1/cheese.js`
                    script.async = true
                    script.onload = () => {
                        if (window.cheese) resolve(window.cheese)
                        else reject(new Error('Cheese SDK did not initialize'))
                    }
                    script.onerror = () => {
                        script.remove()
                        reject(new Error('Could not load Cheese SDK'))
                    }
                    document.head.appendChild(script)
                }),
            onError: (error) => logger.warn('[Cheese] Tracking unavailable:', error)
        })

        const sync = () => {
            const path = route.value.path
            const excluded = ['/admin', '/growni', '/mcp'].some(
                (prefix) => path === prefix || path.startsWith(`${prefix}/`)
            )
            const traits: CheeseTraits = {}
            const profile = store.userProfile
            if (profile?.firstName) traits.firstName = profile.firstName
            if (profile?.lastName) traits.lastName = profile.lastName
            if (profile?.firstName || profile?.lastName) {
                traits.name = [profile.firstName, profile.lastName].filter(Boolean).join(' ')
            }
            if (store.userRegisteredEmail) traits.email = store.userRegisteredEmail
            if (store.userId) traits.email_verified = store.isEmailVerified

            tracking.sync({
                allowed: cookiesEnabledIds.value?.includes('ga') === true && !privacySignal() && !excluded,
                authReady: store.authInitialized && !store.isAuthLoading,
                userId: store.userId ?? null,
                traits,
                path: route.value.fullPath
            })
        }

        const unwatch = watch(
            () => [
                cookiesEnabledIds.value,
                store.authInitialized,
                store.isAuthLoading,
                store.userId,
                store.userRegisteredEmail,
                store.isEmailVerified,
                store.userProfile?.firstName,
                store.userProfile?.lastName,
                route.value.fullPath
            ],
            sync,
            { immediate: true, deep: true, flush: 'post' }
        )
        const removePageHook = nuxtApp.hook('page:finish', sync)
        window.addEventListener('focus', sync)
        nuxtApp.vueApp.onUnmount(() => {
            unwatch()
            removePageHook()
            window.removeEventListener('focus', sync)
            tracking.dispose()
        })
        return {
            provide: {
                cheeseTrack: (
                    name: string,
                    properties: import('@/utils/cheeseTracking').CheeseEventProperties = {}
                ) => {
                    try {
                        sync()
                        void tracking.track(name, properties)
                    } catch (error) {
                        logger.warn('[Cheese] Tracking unavailable:', error)
                    }
                }
            }
        }
    }
})
